import { Box, Stack } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import classnames from 'classnames';
import moment from 'moment';
import React, { ReactNode, useEffect, useState } from 'react';
import { DashboardQueryAnswer } from '../../../contexts/DashboardContext';
import { AnswerQuestionResponse } from '../../../types/search';
import SkeletonLoader from '../../atoms/SkeletonLoader';
import AnswerHeaderLink from '../dashboard-query-answer/AnswerHeaderLink';
import InlineAnswerEditor from '../dashboard-query-answer/InlineAnswerEditor';

const useStyles = makeStyles((theme) => ({
    masonryElement: {
        display: 'inline-block',
        width: 'auto',
        height: 'auto',
        overflow: 'hidden',
        paddingTop: 16,
        paddingBottom: 16,
        borderRadius: 16,
        border: `2px solid ${theme.colors.primary['100']}`,
        background: theme.colors.primary['50'],
        transition: 'all 300ms ease',
    },
    linkOnly: {
        display: 'flex',
        width: '100%',
        padding: 'unset',
        border: 'unset',
        background: 'transparent',
        flexDirection: "row",
        alignItem: 'center',
        justifyContent: 'flex-start',
        "&:hover": {
            border: `unset !important`,
            background: 'transparent !important',
            boxShadow: 'unset !important',
        },
    },
    answerContainer: {
        width: '100%',
        height: 'auto',
        marginLeft: 8,
        marginRight: 8,
        paddingLeft: 8,
        paddingRight: 8,
        border: `1px transparent solid`,
        borderRadius: 12,
    },
    answerBlock: {
        display: 'flex',
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'space-between',
        maxHeight: 910,
        overflow: 'hidden'
    },
}));

const MaxWaitLoadingTime = 15;

const LinkedQueryContainer: React.FC<{
    queryAnswer: DashboardQueryAnswer,
    children?: ReactNode,
    isPublic?: boolean,
    linkOnly?: boolean,
    onEditing?: (queryTitle?: string) => void,
    onEdit?: (answer: AnswerQuestionResponse, history?: any) => void,
    onDelete?: () => void,
}> = ({ queryAnswer, children, isPublic, linkOnly, onEditing, onEdit, onDelete }) => {
    const classes = useStyles();
    const [isEditingAnswer, setIsEditingAnswer] = useState<boolean>(false);
    const [editAnswer, setEditAnswer] = useState<string | null >(null);
    const [showAnswer, setShowAnswer] = useState<boolean>(false);

    const answeredQuestion = queryAnswer.answeredQuestion;

    const saveEditAnswer = (answer: string) => {
        onEdit?.({answer: answer, sources: answeredQuestion.sources, type: answeredQuestion.type});
        setIsEditingAnswer(false);
        setEditAnswer(null);
    }

    useEffect(() => {
        if ('answer' in answeredQuestion)
            setShowAnswer((!!answeredQuestion?.answer || !!answeredQuestion?.type)
                || (!!answeredQuestion?.time && moment().diff(answeredQuestion.time, "seconds") > MaxWaitLoadingTime));
        else
            setShowAnswer(true);
    }, [answeredQuestion]);

    useEffect(() => {
        onEditing?.(isEditingAnswer ? queryAnswer.title : undefined);
    // eslint-disable-next-line
    }, [isEditingAnswer]);

    return (<>
        <Box className={classnames(classes.masonryElement, linkOnly && classes.linkOnly)}>
            <AnswerHeaderLink
                answeredQuestion={answeredQuestion}
                isEditingAnswer={isEditingAnswer}
                isPublic={isPublic}
                linkOnly={linkOnly}
                onEditingAnswer={setIsEditingAnswer}
                onEditAnswer={setEditAnswer}
                onDelete={onDelete}
            />
            {isEditingAnswer ? (
                <Box className={classes.answerBlock} ml={1}>
                    <InlineAnswerEditor
                        currentAnswer={editAnswer || ''}
                        placeHolder={'Add link...'}
                        handleCancel={() => {
                            setIsEditingAnswer(false);
                            setEditAnswer(null);
                        }}
                        handleSave={saveEditAnswer} />
                </Box>
            ) : (!!children) && (
                <Stack className={classes.answerContainer}>
                    <Box className={classes.answerBlock}>
                        {showAnswer ? ( children ) : (
                            <SkeletonLoader variant="regular" rowCount={1} />
                        )}
                    </Box>
                </Stack>
            )}
        </Box>
    </>);
}

export default LinkedQueryContainer;
